import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";

const GREETING = {
  role: "assistant",
  content: "Hi! I'm Lift 👋 Ask me anything about MapLift — how it works, pricing, publishing, or custom domains.",
};

export default function ChatWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading, open]);

  // Hide on the full-screen editor and on published business sites.
  const hidden = /^\/(editor|site)\//.test(location.pathname);
  if (hidden) return null;

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/support/chat", {
        messages: next.filter((m) => m !== GREETING).map(({ role, content }) => ({ role, content })),
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: apiError(e) }]);
    }
    setLoading(false);
  };

  return (
    <div className="fixed bottom-5 right-5 z-[60] font-body">
      {open && (
        <div data-testid="support-chat-panel"
          className="mb-3 w-[350px] max-w-[calc(100vw-2.5rem)] h-[480px] max-h-[70vh] bg-white rounded-2xl border border-border shadow-[0_24px_60px_-12px_rgba(0,0,0,0.28)] flex flex-col overflow-hidden ml-fade-up">
          <div className="bg-[#09090b] px-4 py-3.5 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full ml-ai-btn flex items-center justify-center">
                <MessageCircle size={16} color="#fff" />
              </div>
              <div>
                <div className="text-white font-heading font-semibold text-sm leading-none">Lift Assistant</div>
                <div className="text-white/50 text-[11px] mt-1 leading-none">Typically replies instantly</div>
              </div>
            </div>
            <button data-testid="support-chat-close" onClick={() => setOpen(false)} className="text-white/60 hover:text-white">
              <X size={18} />
            </button>
          </div>

          <div data-testid="support-chat-messages" className="flex-1 overflow-auto ml-scrollbar p-4 space-y-3 bg-[#fafafa]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-line ${
                  m.role === "user"
                    ? "bg-[#09090b] text-white rounded-br-sm"
                    : "bg-white border border-border text-[#09090b] rounded-bl-sm"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-border rounded-2xl rounded-bl-sm px-3.5 py-3">
                  <Loader2 size={15} className="animate-spin text-[#a1a1aa]" />
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className="p-3 border-t border-border bg-white flex items-center gap-2">
            <input
              data-testid="support-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              placeholder="Ask a question..."
              className="flex-1 bg-[#f4f4f5] rounded-full px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-sky-400"
            />
            <button data-testid="support-chat-send" onClick={send} disabled={loading || !input.trim()}
              className="w-10 h-10 rounded-full ml-ai-btn flex items-center justify-center shrink-0 disabled:opacity-50">
              <Send size={16} color="#fff" />
            </button>
          </div>
        </div>
      )}

      <button
        data-testid="support-chat-toggle"
        onClick={() => setOpen((o) => !o)}
        className="w-14 h-14 rounded-full ml-ai-btn shadow-[0_12px_30px_-6px_rgba(37,99,235,0.5)] flex items-center justify-center ml-auto hover:scale-105 transition-transform"
        aria-label="Chat with support"
      >
        {open ? <X size={22} color="#fff" /> : <MessageCircle size={24} color="#fff" />}
      </button>
    </div>
  );
}
